import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

/**
 * CartContext
 *
 * Items can be of two kinds:
 *   { kind: 'panel',   id, name, price, sku, qty }
 *   { kind: 'custom',  id, name, markers: [{id,name,price}], price (sum), qty }
 *
 * Persisted to localStorage under 'as_cart_v1'.
 */

const CartContext = createContext(null);

const STORAGE_KEY = 'as_cart_v1';

const readStorage = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const writeStorage = (items) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    /* swallow */
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => readStorage());

  // Toast / flash add
  const [flash, setFlash] = useState(null); // { name }

  useEffect(() => {
    writeStorage(items);
  }, [items]);

  const totalQty = useMemo(
    () => items.reduce((s, it) => s + (it.qty || 1), 0),
    [items]
  );

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + it.price * (it.qty || 1), 0),
    [items]
  );

  const addPanel = useCallback((panel) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.kind === 'panel' && it.id === panel.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: (next[idx].qty || 1) + 1 };
        return next;
      }
      return [
        ...prev,
        {
          kind: 'panel',
          id: panel.id,
          name: panel.name,
          price: panel.price,
          sku: panel.sku,
          badge: panel.badge,
          qty: 1,
        },
      ];
    });
    setFlash({ name: panel.name, ts: Date.now() });
  }, []);

  const addCustomPanel = useCallback((markers) => {
    if (!markers || markers.length === 0) return null;
    const sum = markers.reduce((s, m) => s + m.price, 0);
    const id = `custom-${Date.now().toString(36)}`;
    const item = {
      kind: 'custom',
      id,
      name: `Custom panel — ${markers.length} marker${markers.length > 1 ? 's' : ''}`,
      price: sum,
      markers: markers.map((m) => ({ id: m.id, name: m.name, price: m.price })),
      qty: 1,
    };
    setItems((prev) => [...prev, item]);
    setFlash({ name: item.name, ts: Date.now() });
    return item;
  }, []);

  const updateQty = useCallback((id, qty) => {
    setItems((prev) =>
      prev
        .map((it) => (it.id === id ? { ...it, qty: Math.max(1, qty) } : it))
        .filter(Boolean)
    );
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  // Auto-clear flash after 2.4s
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 2400);
    return () => clearTimeout(t);
  }, [flash]);

  const value = useMemo(
    () => ({
      items,
      totalQty,
      subtotal,
      addPanel,
      addCustomPanel,
      updateQty,
      removeItem,
      clear,
      flash,
    }),
    [items, totalQty, subtotal, addPanel, addCustomPanel, updateQty, removeItem, clear, flash]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used inside <CartProvider>');
  }
  return ctx;
};

export default CartContext;
